import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useAdmin } from "../contexts/AdminContext";
import { getVisitorStats, resetVisitorStats } from "../utils/visitorTracker";

const clonePersonal = (p) => ({
  ...p,
  cv:
    typeof p.cv === "object" && p.cv !== null
      ? { ...p.cv }
      : p.cv,
});

const deepClone = (value) => {
  if (value === undefined || value === null) return value;
  try {
    if (typeof structuredClone === "function") {
      return structuredClone(value);
    }
  } catch (_) {
    /* non-cloneable values */
  }
  return JSON.parse(JSON.stringify(value));
};

const AdminDashboard = ({ onClose }) => {
  const {
    portfolioData,
    updateSection,
    logout,
    isLoading,
  } = useAdmin();

  const [activeTab, setActiveTab] = useState("analytics");
  const [editingItem, setEditingItem] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [visitorStats, setVisitorStats] = useState({
    totalVisits: 0,
    uniqueVisitors: 0,
    todayVisits: 0,
    thisWeekVisits: 0,
    thisMonthVisits: 0,
    lastVisit: null
  });
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [saveStatus, setSaveStatus] = useState('');
  const [personalDraft, setPersonalDraft] = useState(null);
  const [personalDirty, setPersonalDirty] = useState(false);
  const [personalSaving, setPersonalSaving] = useState(false);
  const [aboutDraft, setAboutDraft] = useState(null);
  const [aboutDirty, setAboutDirty] = useState(false);
  const [aboutSaving, setAboutSaving] = useState(false);
  const [skillsDraft, setSkillsDraft] = useState(null);
  const [skillsDirty, setSkillsDirty] = useState(false);
  const [skillsSaving, setSkillsSaving] = useState(false);
  const [contactDraft, setContactDraft] = useState(null);
  const [contactDirty, setContactDirty] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const [experienceDraft, setExperienceDraft] = useState(null);
  const [experienceDirty, setExperienceDirty] = useState(false);
  const [experienceSaving, setExperienceSaving] = useState(false);
  const [projectsDraft, setProjectsDraft] = useState(null);
  const [projectsDirty, setProjectsDirty] = useState(false);
  const [projectsSaving, setProjectsSaving] = useState(false);
  const emptyProjectRef = useRef({});
  const [messages, setMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  useLayoutEffect(() => {
    if (!portfolioData) return;
    if (activeTab === "personal" && portfolioData.personal) {
      setPersonalDraft((d) => (d === null ? clonePersonal(portfolioData.personal) : d));
    }
    if (activeTab === "about" && portfolioData.about) {
      setAboutDraft((d) => (d === null ? deepClone(portfolioData.about) : d));
    }
    if (activeTab === "skills" && portfolioData.skills) {
      setSkillsDraft((d) => (d === null ? deepClone(portfolioData.skills) : d));
    }
    if (activeTab === "contact" && portfolioData.contact) {
      setContactDraft((d) => (d === null ? deepClone(portfolioData.contact) : d));
    }
    if (activeTab === "experience" && portfolioData.experience) {
      setExperienceDraft((d) => (d === null ? deepClone(portfolioData.experience) : d));
    }
    if (activeTab === "projects" && portfolioData.projects) {
      setProjectsDraft((d) => (d === null ? deepClone(portfolioData.projects) : d));
    }
  }, [activeTab, portfolioData]);

  // Refresh stats when analytics tab is opened
  useEffect(() => {
    const loadStats = async () => {
      if (activeTab === "analytics") {
        setIsLoadingStats(true);
        try {
          const stats = await getVisitorStats();
          setVisitorStats(stats);
        } catch (error) {
          console.error('Error loading visitor stats:', error);
        } finally {
          setIsLoadingStats(false);
        }
      }
    };
    loadStats();
  }, [activeTab]);

  // Load messages when messages tab is opened
  useEffect(() => {
    const loadMessages = async () => {
      if (activeTab === "messages") {
        setIsLoadingMessages(true);
        try {
          const { collection, query, orderBy, getDocs } = await import('firebase/firestore');
          const { db } = await import('../config/firebase');
          
          const messagesQuery = query(
            collection(db, 'contactMessages'),
            orderBy('timestamp', 'desc')
          );
          const querySnapshot = await getDocs(messagesQuery);
          const messagesData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            timestamp: doc.data().timestamp?.toDate?.()
          }));
          setMessages(messagesData);
        } catch (error) {
          console.error('Error loading messages:', error);
        } finally {
          setIsLoadingMessages(false);
        }
      }
    };
    loadMessages();
  }, [activeTab]);

  const tabs = [
    { id: "analytics", name: "Analytics", icon: "fas fa-chart-line" },
    { id: "messages", name: "Messages", icon: "fas fa-inbox" },
    { id: "personal", name: "Personal Info", icon: "fas fa-user" },
    { id: "about", name: "About", icon: "fas fa-info-circle" },
    { id: "skills", name: "Skills", icon: "fas fa-code" },
    { id: "projects", name: "Projects", icon: "fas fa-project-diagram" },
    { id: "experience", name: "Experience", icon: "fas fa-briefcase" },
    { id: "contact", name: "Contact", icon: "fas fa-envelope" },
  ];

  const handleLogout = () => {
    logout();
    onClose();
  };

  const renderPersonalInfo = () => {
    const p = personalDraft ?? portfolioData.personal;

    const handleDraftChange = (field, value) => {
      setPersonalDraft((prev) => ({
        ...(prev ?? clonePersonal(portfolioData.personal)),
        [field]: value,
      }));
      setPersonalDirty(true);
    };

    const handleSavePersonal = async () => {
      const data = personalDraft ?? portfolioData.personal;
      setPersonalSaving(true);
      try {
        await updateSection("personal", data);
        setPersonalDirty(false);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(""), 2000);
      } catch (error) {
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(""), 2000);
      } finally {
        setPersonalSaving(false);
      }
    };

    return (
      <div className="admin-section">
        <div className="section-header">
          <div>
            <h3><i className="fas fa-user-circle"></i> Personal Information</h3>
            <p className="section-subtitle">Edit your details, then save to update the live portfolio</p>
          </div>
          {saveStatus && (
            <div className={`save-indicator save-${saveStatus}`}>
              <i className={`fas fa-${saveStatus === 'saved' ? 'check-circle' : 'exclamation-circle'}`}></i>
              {saveStatus === 'saved' ? 'Saved' : 'Error'}
            </div>
          )}
        </div>

        <div className="form-card">
          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-id-card"></i> Basic Info</h4>
            <div className="form-grid">
              <div className="form-group">
                <label><i className="fas fa-user"></i> Full Name *</label>
                <input
                  type="text"
                  value={p.name}
                  onChange={(e) => handleDraftChange('name', e.target.value)}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div className="form-group">
                <label><i className="fas fa-briefcase"></i> Professional Title *</label>
                <input
                  type="text"
                  value={p.title}
                  onChange={(e) => handleDraftChange('title', e.target.value)}
                  placeholder="e.g., Full Stack Developer"
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label><i className="fas fa-align-left"></i> Professional Description *</label>
              <textarea
                value={p.description}
                onChange={(e) => handleDraftChange('description', e.target.value)}
                placeholder="Brief description of your expertise and experience..."
                rows="4"
                required
              />
              <span className="char-count">{p.description?.length || 0} characters</span>
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-address-card"></i> Contact Details</h4>
            <div className="form-grid">
              <div className="form-group">
                <label><i className="fas fa-envelope"></i> Email Address *</label>
                <input
                  type="email"
                  value={p.email}
                  onChange={(e) => handleDraftChange('email', e.target.value)}
                  placeholder="email@example.com"
                  required
                />
              </div>
              <div className="form-group">
                <label><i className="fas fa-phone"></i> Phone Number</label>
                <input
                  type="text"
                  value={p.phone}
                  onChange={(e) => handleDraftChange('phone', e.target.value)}
                  placeholder="+1 234 567 8900"
                />
              </div>
              <div className="form-group">
                <label><i className="fas fa-map-marker-alt"></i> Location</label>
                <input
                  type="text"
                  value={p.location}
                  onChange={(e) => handleDraftChange('location', e.target.value)}
                  placeholder="City, Country"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-link"></i> Social Links & CV</h4>
            <div className="form-grid">
              <div className="form-group">
                <label><i className="fab fa-whatsapp"></i> WhatsApp URL</label>
                <input
                  type="url"
                  value={p.whatsapp}
                  onChange={(e) => handleDraftChange('whatsapp', e.target.value)}
                  placeholder="https://wa.me/..."
                />
              </div>
              <div className="form-group">
                <label><i className="fab fa-linkedin"></i> LinkedIn URL</label>
                <input
                  type="url"
                  value={p.linkedin}
                  onChange={(e) => handleDraftChange('linkedin', e.target.value)}
                  placeholder="https://linkedin.com/in/..."
                />
              </div>
              <div className="form-group">
                <label><i className="fab fa-github"></i> GitHub URL</label>
                <input
                  type="url"
                  value={p.github}
                  onChange={(e) => handleDraftChange('github', e.target.value)}
                  placeholder="https://github.com/..."
                />
              </div>
              <div className="form-group">
                <label><i className="fas fa-globe"></i> Portfolio URL</label>
                <input
                  type="url"
                  value={p.portfolio}
                  onChange={(e) => handleDraftChange('portfolio', e.target.value)}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label><i className="fas fa-file-alt"></i> CV (English) URL</label>
                <input
                  type="url"
                  value={p.cv?.english || p.cv}
                  onChange={(e) => {
                    setPersonalDraft((prev) => {
                      const base = prev ?? clonePersonal(portfolioData.personal);
                      const cvValue =
                        typeof base.cv === "object" && base.cv !== null
                          ? { ...base.cv, english: e.target.value }
                          : { english: e.target.value, french: e.target.value };
                      return { ...base, cv: cvValue };
                    });
                    setPersonalDirty(true);
                  }}
                  placeholder="https://drive.google.com/..."
                />
              </div>
              <div className="form-group">
                <label><i className="fas fa-file-alt"></i> CV (French) URL</label>
                <input
                  type="url"
                  value={p.cv?.french || p.cv}
                  onChange={(e) => {
                    setPersonalDraft((prev) => {
                      const base = prev ?? clonePersonal(portfolioData.personal);
                      const cvValue =
                        typeof base.cv === "object" && base.cv !== null
                          ? { ...base.cv, french: e.target.value }
                          : { english: base.cv, french: e.target.value };
                      return { ...base, cv: cvValue };
                    });
                    setPersonalDirty(true);
                  }}
                  placeholder="https://drive.google.com/..."
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!personalDirty || personalSaving}
              onClick={handleSavePersonal}
            >
              <i className="fas fa-save"></i>
              {personalSaving ? " Saving…" : " Save changes"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderAbout = () => {
    const a = aboutDraft ?? portfolioData.about;

    const addStat = () => {
      setAboutDraft({
        ...a,
        stats: [...a.stats, { number: "0", label: "New Stat" }],
      });
      setAboutDirty(true);
    };

    const removeStat = (index) => {
      if (window.confirm("Are you sure you want to remove this statistic?")) {
        setAboutDraft({
          ...a,
          stats: a.stats.filter((_, i) => i !== index),
        });
        setAboutDirty(true);
      }
    };

    const handleSaveAbout = async () => {
      const data = aboutDraft ?? portfolioData.about;
      setAboutSaving(true);
      try {
        await updateSection("about", data);
        setAboutDirty(false);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(""), 2000);
      } catch (error) {
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(""), 2000);
      } finally {
        setAboutSaving(false);
      }
    };

    return (
      <div className="admin-section">
        <div className="section-header">
          <div>
            <h3><i className="fas fa-info-circle"></i> About Section</h3>
            <p className="section-subtitle">Edit your bio and stats, then save to publish</p>
          </div>
          {saveStatus && (
            <div className={`save-indicator save-${saveStatus}`}>
              <i className={`fas fa-${saveStatus === "saved" ? "check-circle" : "exclamation-circle"}`}></i>
              {saveStatus === "saved" ? "Saved" : "Error"}
            </div>
          )}
        </div>

        <div className="form-card">
          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-heading"></i> Section Content</h4>
            <div className="form-group">
              <label><i className="fas fa-tag"></i> Section Title</label>
              <input
                type="text"
                value={a.title}
                onChange={(e) => {
                  setAboutDraft({ ...a, title: e.target.value });
                  setAboutDirty(true);
                }}
                placeholder="e.g., About Me"
              />
            </div>
            <div className="form-group">
              <label><i className="fas fa-align-left"></i> Description (Part 1)</label>
              <textarea
                value={a.description1}
                onChange={(e) => {
                  setAboutDraft({ ...a, description1: e.target.value });
                  setAboutDirty(true);
                }}
                rows="4"
                placeholder="First paragraph of your bio..."
              />
              <span className="char-count">{a.description1?.length || 0} characters</span>
            </div>
            <div className="form-group">
              <label><i className="fas fa-align-left"></i> Description (Part 2)</label>
              <textarea
                value={a.description2}
                onChange={(e) => {
                  setAboutDraft({ ...a, description2: e.target.value });
                  setAboutDirty(true);
                }}
                rows="4"
                placeholder="Second paragraph of your bio..."
              />
              <span className="char-count">{a.description2?.length || 0} characters</span>
            </div>
          </div>

          <div className="form-section">
            <div className="subsection-header">
              <h4 className="form-section-title"><i className="fas fa-chart-bar"></i> Achievement Statistics</h4>
              <button className="btn-action btn-add" onClick={addStat}>
                <i className="fas fa-plus"></i>
                <span>Add Stat</span>
              </button>
            </div>
            <div className="stats-grid">
              {a.stats.map((stat, index) => (
                <div key={index} className="stat-item-card">
                  <div className="stat-item-header">
                    <span className="stat-index">#{index + 1}</span>
                    <button
                      className="btn-icon btn-danger-ghost"
                      onClick={() => removeStat(index)}
                      title="Remove stat"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  <div className="form-group">
                    <label><i className="fas fa-hashtag"></i> Number/Value</label>
                    <input
                      type="text"
                      value={stat.number}
                      onChange={(e) => {
                        const newStats = [...a.stats];
                        newStats[index] = { ...newStats[index], number: e.target.value };
                        setAboutDraft({ ...a, stats: newStats });
                        setAboutDirty(true);
                      }}
                      placeholder="e.g., 50+, 100%, etc."
                    />
                  </div>
                  <div className="form-group">
                    <label><i className="fas fa-tag"></i> Label</label>
                    <input
                      type="text"
                      value={stat.label}
                      onChange={(e) => {
                        const newStats = [...a.stats];
                        newStats[index] = { ...newStats[index], label: e.target.value };
                        setAboutDraft({ ...a, stats: newStats });
                        setAboutDirty(true);
                      }}
                      placeholder="e.g., Projects Completed"
                    />
                  </div>
                </div>
              ))}
            </div>
            {a.stats.length === 0 && (
              <div className="empty-state">
                <i className="fas fa-chart-bar"></i>
                <p>No statistics yet. Click "Add Stat" to create one.</p>
              </div>
            )}
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!aboutDirty || aboutSaving}
              onClick={handleSaveAbout}
            >
              <i className="fas fa-save"></i>
              {aboutSaving ? " Saving…" : " Save changes"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSkills = () => {
    const sk = skillsDraft ?? portfolioData.skills;
    const additionalSkills = sk.additionalSkills || [];

    const removeCategory = (index) => {
      if (window.confirm("Are you sure you want to remove this skill category?")) {
        setSkillsDraft({
          ...sk,
          categories: sk.categories.filter((_, i) => i !== index),
        });
        setSkillsDirty(true);
      }
    };

    const handleSaveSkills = async () => {
      const data = skillsDraft ?? portfolioData.skills;
      setSkillsSaving(true);
      try {
        await updateSection("skills", data);
        setSkillsDirty(false);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(""), 2000);
      } catch (error) {
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(""), 2000);
      } finally {
        setSkillsSaving(false);
      }
    };

    return (
      <div className="admin-section">
        <div className="section-header">
          <div>
            <h3><i className="fas fa-code"></i> Skills Management</h3>
            <p className="section-subtitle">Edit skills and categories, then save to publish</p>
          </div>
          <div className="section-actions">
            <button
              className="btn-action btn-add"
              onClick={() => setShowAddForm(true)}
            >
              <i className="fas fa-plus"></i>
              <span>Add Category</span>
            </button>
          </div>
          {saveStatus && (
            <div className={`save-indicator save-${saveStatus}`}>
              <i className={`fas fa-${saveStatus === "saved" ? "check-circle" : "exclamation-circle"}`}></i>
              {saveStatus === "saved" ? "Saved" : "Error"}
            </div>
          )}
        </div>

        <div className="form-card">
          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-heading"></i> Section Content</h4>
            <div className="form-grid">
              <div className="form-group">
                <label><i className="fas fa-tag"></i> Section Title</label>
                <input
                  type="text"
                  value={sk.title}
                  onChange={(e) => {
                    setSkillsDraft({ ...sk, title: e.target.value });
                    setSkillsDirty(true);
                  }}
                  placeholder="e.g., Technical Skills"
                />
              </div>
              <div className="form-group">
                <label><i className="fas fa-text-height"></i> Section Subtitle</label>
                <input
                  type="text"
                  value={sk.subtitle}
                  onChange={(e) => {
                    setSkillsDraft({ ...sk, subtitle: e.target.value });
                    setSkillsDirty(true);
                  }}
                  placeholder="e.g., My expertise and technologies I work with"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-layer-group"></i> Skill Categories</h4>
            <div className="skill-categories-grid">
              {sk.categories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="skill-category-card">
                  <div className="skill-category-header">
                    <div className="category-icon">{category.icon}</div>
                    <h5>{category.category}</h5>
                    <div className="category-actions">
                      <button
                        className="btn-icon btn-primary-ghost"
                        onClick={() =>
                          setEditingItem({
                            type: "skillCategory",
                            index: categoryIndex,
                            data: category,
                          })
                        }
                        title="Edit category"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button
                        className="btn-icon btn-danger-ghost"
                        onClick={() => removeCategory(categoryIndex)}
                        title="Delete category"
                      >
                        <i className="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </div>
                  <p className="category-description">{category.description}</p>
                  <div className="skills-tags">
                    {category.skills.map((skill, idx) => (
                      <span key={idx} className="skill-tag">
                        {typeof skill === "string" ? skill : skill.name}
                      </span>
                    ))}
                  </div>
                  <div className="category-footer">
                    <span className="skill-count">
                      <i className="fas fa-check-circle"></i> {category.skills.length} skills
                    </span>
                  </div>
                </div>
              ))}
            </div>
            {sk.categories.length === 0 && (
              <div className="empty-state">
                <i className="fas fa-layer-group"></i>
                <p>No skill categories yet. Click "Add Category" to create one.</p>
              </div>
            )}
          </div>

          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-plus-circle"></i> Additional Expertise</h4>
            <div className="form-group">
              <label><i className="fas fa-heading"></i> Section Title</label>
              <input
                type="text"
                value={sk.additionalExpertise || ""}
                onChange={(e) => {
                  setSkillsDraft({ ...sk, additionalExpertise: e.target.value });
                  setSkillsDirty(true);
                }}
                placeholder="e.g., Additional Expertise"
              />
            </div>
            <div className="form-group">
              <label><i className="fas fa-tags"></i> Additional Skills (comma-separated)</label>
              <textarea
                value={additionalSkills.join(", ")}
                onChange={(e) => {
                  setSkillsDraft({
                    ...sk,
                    additionalSkills: e.target.value
                      .split(",")
                      .map((s) => s.trim())
                      .filter((s) => s),
                  });
                  setSkillsDirty(true);
                }}
                rows="4"
                placeholder="Git, Docker, AWS, Agile, etc."
              />
              <span className="char-count">{additionalSkills.length} skills</span>
            </div>
            <div className="skills-preview">
              {additionalSkills.map((skill, idx) => (
                <span key={idx} className="skill-tag-preview">
                  {typeof skill === "string" ? skill : skill.name || skill}
                </span>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!skillsDirty || skillsSaving}
              onClick={handleSaveSkills}
            >
              <i className="fas fa-save"></i>
              {skillsSaving ? " Saving…" : " Save changes"}
            </button>
          </div>
        </div>

        {editingItem && editingItem.type === "skillCategory" && (
          <div className="edit-modal">
            <div className="edit-modal-content">
              <h4>Edit Skill Category</h4>
              <SkillCategoryForm
                category={editingItem.data}
                onSave={(updatedCategory) => {
                  const base = skillsDraft ?? deepClone(portfolioData.skills);
                  const newCategories = [...base.categories];
                  newCategories[editingItem.index] = updatedCategory;
                  setSkillsDraft({ ...base, categories: newCategories });
                  setSkillsDirty(true);
                  setEditingItem(null);
                }}
                onCancel={() => setEditingItem(null)}
              />
            </div>
          </div>
        )}

        {showAddForm && (
          <div className="add-form">
            <h4>Add New Skill Category</h4>
            <SkillCategoryForm
              category={{}}
              onSave={(newCategory) => {
                const base = skillsDraft ?? deepClone(portfolioData.skills);
                setSkillsDraft({
                  ...base,
                  categories: [...base.categories, newCategory],
                });
                setSkillsDirty(true);
                setShowAddForm(false);
              }}
              onCancel={() => setShowAddForm(false)}
            />
          </div>
        )}
      </div>
    );
  };

  const renderContact = () => {
    const c = contactDraft ?? portfolioData.contact;

    const removeMethod = (index) => {
      if (window.confirm("Are you sure you want to remove this contact method?")) {
        setContactDraft({
          ...c,
          methods: c.methods.filter((_, i) => i !== index),
        });
        setContactDirty(true);
      }
    };

    const handleSaveContact = async () => {
      const data = contactDraft ?? portfolioData.contact;
      setContactSaving(true);
      try {
        await updateSection("contact", data);
        setContactDirty(false);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(""), 2000);
      } catch (error) {
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(""), 2000);
      } finally {
        setContactSaving(false);
      }
    };

    return (
      <div className="admin-section">
        <div className="section-header">
          <div>
            <h3><i className="fas fa-envelope"></i> Contact Section</h3>
            <p className="section-subtitle">Edit methods and form settings, then save to publish</p>
          </div>
          <div className="section-actions">
            <button
              className="btn-action btn-add"
              onClick={() => setShowAddForm(true)}
            >
              <i className="fas fa-plus"></i>
              <span>Add Method</span>
            </button>
          </div>
          {saveStatus && (
            <div className={`save-indicator save-${saveStatus}`}>
              <i className={`fas fa-${saveStatus === "saved" ? "check-circle" : "exclamation-circle"}`}></i>
              {saveStatus === "saved" ? "Saved" : "Error"}
            </div>
          )}
        </div>

        <div className="form-card">
          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-heading"></i> Section Content</h4>
            <div className="form-grid">
              <div className="form-group">
                <label><i className="fas fa-tag"></i> Section Title</label>
                <input
                  type="text"
                  value={c.title}
                  onChange={(e) => {
                    setContactDraft({ ...c, title: e.target.value });
                    setContactDirty(true);
                  }}
                  placeholder="e.g., Get In Touch"
                />
              </div>
              <div className="form-group">
                <label><i className="fas fa-text-height"></i> Section Subtitle</label>
                <input
                  type="text"
                  value={c.subtitle}
                  onChange={(e) => {
                    setContactDraft({ ...c, subtitle: e.target.value });
                    setContactDirty(true);
                  }}
                  placeholder="e.g., Let's work together"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-address-book"></i> Contact Methods</h4>
            <div className="contact-methods-grid">
              {c.methods.map((method, index) => (
                <div key={index} className="contact-method-card">
                  <div className="method-icon-wrapper">
                    <i className={method.icon}></i>
                  </div>
                  <div className="method-details">
                    <h5>{method.name}</h5>
                    <p className="method-url">{method.url}</p>
                  </div>
                  <div className="method-actions">
                    <button
                      className="btn-icon btn-primary-ghost"
                      onClick={() =>
                        setEditingItem({
                          type: "contactMethod",
                          index: index,
                          data: method,
                        })
                      }
                      title="Edit method"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn-icon btn-danger-ghost"
                      onClick={() => removeMethod(index)}
                      title="Delete method"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {c.methods.length === 0 && (
              <div className="empty-state">
                <i className="fas fa-address-book"></i>
                <p>No contact methods yet. Click "Add Method" to create one.</p>
              </div>
            )}
          </div>

          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-paper-plane"></i> Contact Form Settings</h4>
            <div className="form-group">
              <label><i className="fas fa-link"></i> Form Action URL (FormSpree, etc.)</label>
              <input
                type="url"
                value={c.form.action}
                onChange={(e) => {
                  setContactDraft({
                    ...c,
                    form: { ...c.form, action: e.target.value },
                  });
                  setContactDirty(true);
                }}
                placeholder="https://formspree.io/f/..."
              />
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label><i className="fas fa-info-circle"></i> Form Hint Text</label>
                <input
                  type="text"
                  value={c.form.hint}
                  onChange={(e) => {
                    setContactDraft({
                      ...c,
                      form: { ...c.form, hint: e.target.value },
                    });
                    setContactDirty(true);
                  }}
                  placeholder="e.g., Or send me a message"
                />
              </div>
              <div className="form-group">
                <label><i className="fas fa-paper-plane"></i> Submit Button Text</label>
                <input
                  type="text"
                  value={c.form.submitText}
                  onChange={(e) => {
                    setContactDraft({
                      ...c,
                      form: { ...c.form, submitText: e.target.value },
                    });
                    setContactDirty(true);
                  }}
                  placeholder="e.g., Send Message"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-list-alt"></i> Form Fields</h4>
            <div className="form-fields-list">
              {c.form.fields.map((field, index) => (
                <div key={index} className="form-field-item">
                  <div className="field-number">{index + 1}</div>
                  <div className="field-info">
                    <h6>{field.label}</h6>
                    <p>{field.placeholder}</p>
                  </div>
                  <div className="field-actions">
                    <button
                      className="btn-icon btn-primary-ghost"
                      onClick={() =>
                        setEditingItem({
                          type: "formField",
                          index: index,
                          data: field,
                        })
                      }
                      title="Edit field"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn-icon btn-danger-ghost"
                      onClick={() => {
                        if (window.confirm("Remove this form field?")) {
                          setContactDraft({
                            ...c,
                            form: {
                              ...c.form,
                              fields: c.form.fields.filter((_, i) => i !== index),
                            },
                          });
                          setContactDirty(true);
                        }
                      }}
                      title="Delete field"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!contactDirty || contactSaving}
              onClick={handleSaveContact}
            >
              <i className="fas fa-save"></i>
              {contactSaving ? " Saving…" : " Save changes"}
            </button>
          </div>
        </div>

        {editingItem && editingItem.type === "contactMethod" && (
          <div className="edit-modal">
            <div className="edit-modal-content">
              <h4>Edit Contact Method</h4>
              <ContactMethodForm
                method={editingItem.data}
                onSave={(updatedMethod) => {
                  const base = contactDraft ?? deepClone(portfolioData.contact);
                  const newMethods = [...base.methods];
                  newMethods[editingItem.index] = updatedMethod;
                  setContactDraft({ ...base, methods: newMethods });
                  setContactDirty(true);
                  setEditingItem(null);
                }}
                onCancel={() => setEditingItem(null)}
              />
            </div>
          </div>
        )}

        {editingItem && editingItem.type === "formField" && (
          <div className="edit-modal">
            <div className="edit-modal-content">
              <h4>Edit Form Field</h4>
              <FormFieldForm
                field={editingItem.data}
                onSave={(updatedField) => {
                  const base = contactDraft ?? deepClone(portfolioData.contact);
                  const newFields = [...base.form.fields];
                  newFields[editingItem.index] = updatedField;
                  setContactDraft({
                    ...base,
                    form: { ...base.form, fields: newFields },
                  });
                  setContactDirty(true);
                  setEditingItem(null);
                }}
                onCancel={() => setEditingItem(null)}
              />
            </div>
          </div>
        )}

        {showAddForm && (
          <div className="add-form">
            <h4>Add New Contact Method</h4>
            <ContactMethodForm
              method={{}}
              onSave={(newMethod) => {
                const base = contactDraft ?? deepClone(portfolioData.contact);
                setContactDraft({
                  ...base,
                  methods: [...base.methods, newMethod],
                });
                setContactDirty(true);
                setShowAddForm(false);
              }}
              onCancel={() => setShowAddForm(false)}
            />
          </div>
        )}
      </div>
    );
  };

  const renderProjects = () => {
    const pr = projectsDraft ?? portfolioData.projects;
    const items = pr.items || [];

    const projectsByCategory = {
      all: items,
      mobile: items.filter((p) => p.category === "mobile"),
      web: items.filter((p) => p.category === "web"),
    };

    const handleSaveProjects = async () => {
      const data = projectsDraft ?? portfolioData.projects;
      setProjectsSaving(true);
      try {
        await updateSection("projects", data);
        setProjectsDirty(false);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(""), 2000);
      } catch (error) {
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(""), 2000);
      } finally {
        setProjectsSaving(false);
      }
    };

    return (
      <div className="admin-section">
        <div className="section-header">
          <div>
            <h3><i className="fas fa-project-diagram"></i> Projects Portfolio</h3>
            <p className="section-subtitle">Edit projects and section text, then save to publish</p>
          </div>
          <div className="section-actions">
            <button
              className="btn-action btn-add"
              onClick={() => setShowAddForm(true)}
            >
              <i className="fas fa-plus"></i>
              <span>Add Project</span>
            </button>
          </div>
          {saveStatus && (
            <div className={`save-indicator save-${saveStatus}`}>
              <i className={`fas fa-${saveStatus === "saved" ? "check-circle" : "exclamation-circle"}`}></i>
              {saveStatus === "saved" ? "Saved" : "Error"}
            </div>
          )}
        </div>

        <div className="form-card" style={{ marginBottom: "1.25rem" }}>
          <div className="form-section">
            <h4 className="form-section-title"><i className="fas fa-heading"></i> Section header</h4>
            <div className="form-grid">
              <div className="form-group">
                <label>Projects section title</label>
                <input
                  type="text"
                  value={pr.title}
                  onChange={(e) => {
                    setProjectsDraft({ ...pr, title: e.target.value });
                    setProjectsDirty(true);
                  }}
                  placeholder="Projects"
                />
              </div>
              <div className="form-group">
                <label>Projects section subtitle</label>
                <input
                  type="text"
                  value={pr.subtitle}
                  onChange={(e) => {
                    setProjectsDraft({ ...pr, subtitle: e.target.value });
                    setProjectsDirty(true);
                  }}
                  placeholder="Short subtitle under the heading"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="projects-stats">
          <div className="project-stat">
            <span className="stat-value">{projectsByCategory.all.length}</span>
            <span className="stat-label">Total Projects</span>
          </div>
          <div className="project-stat">
            <span className="stat-value">{projectsByCategory.mobile.length}</span>
            <span className="stat-label">Mobile Apps</span>
          </div>
          <div className="project-stat">
            <span className="stat-value">{projectsByCategory.web.length}</span>
            <span className="stat-label">Web Apps</span>
          </div>
          <div className="project-stat">
            <span className="stat-value">
              {items.filter((p) => p.status === "Live").length}
            </span>
            <span className="stat-label">Live Projects</span>
          </div>
        </div>

        {showAddForm && (
          <div className="add-form">
            <h4>Add New Project</h4>
            <ProjectForm
              project={emptyProjectRef.current}
              onSave={(project) => {
                const base = projectsDraft ?? deepClone(portfolioData.projects);
                setProjectsDraft({
                  ...base,
                  items: [...(base.items || []), project],
                });
                setProjectsDirty(true);
                setShowAddForm(false);
              }}
              onCancel={() => setShowAddForm(false)}
            />
          </div>
        )}

        <div className="projects-grid">
          {items.map((project, index) => (
            <div key={index} className="project-admin-card">
              <div className="project-card-header">
                <div className="project-icon">{project.icon}</div>
                <div className="project-title-area">
                  <h5>{project.title}</h5>
                  <div className="project-badges">
                    <span className={`status-badge-admin ${project.status.toLowerCase()}`}>
                      {project.status}
                    </span>
                    <span className="category-badge">{project.category}</span>
                  </div>
                </div>
                <div className="project-card-actions">
                  <button
                    className="btn-icon btn-primary-ghost"
                    onClick={() =>
                      setEditingItem({ type: "project", index, data: project })
                    }
                    title="Edit project"
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    className="btn-icon btn-danger-ghost"
                    onClick={() => {
                      if (window.confirm("Delete this project from the draft? Save changes to sync to Firestore.")) {
                        const base = projectsDraft ?? deepClone(portfolioData.projects);
                        setProjectsDraft({
                          ...base,
                          items: base.items.filter((_, i) => i !== index),
                        });
                        setProjectsDirty(true);
                      }
                    }}
                    title="Delete project"
                  >
                    <i className="fas fa-trash-alt"></i>
                  </button>
                </div>
              </div>
              <p className="project-description">{project.description}</p>
              <div className="project-tech-stack">
                {project.tech?.slice(0, 4).map((tech, idx) => (
                  <span key={idx} className="tech-badge">
                    {typeof tech === "string" ? tech : tech.name || tech}
                  </span>
                ))}
                {project.tech?.length > 4 && (
                  <span className="tech-badge-more">+{project.tech.length - 4} more</span>
                )}
              </div>
              <div className="project-card-footer">
                <div className="project-meta">
                  <span><i className="fas fa-star"></i> {project.features?.length || 0} features</span>
                  <span><i className="fas fa-link"></i> {project.links?.length || 0} links</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {items.length === 0 && (
          <div className="empty-state">
            <i className="fas fa-project-diagram"></i>
            <p>No projects yet. Click "Add Project" to create one.</p>
          </div>
        )}

        <div className="form-card" style={{ marginTop: "1.25rem" }}>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!projectsDirty || projectsSaving}
              onClick={handleSaveProjects}
            >
              <i className="fas fa-save"></i>
              {projectsSaving ? " Saving…" : " Save changes"}
            </button>
          </div>
        </div>

        {editingItem && editingItem.type === "project" && (
          <div className="edit-modal">
            <div className="edit-modal-content">
              <h4>Edit Project</h4>
              <ProjectForm
                project={editingItem.data}
                onSave={(updatedProject) => {
                  const base = projectsDraft ?? deepClone(portfolioData.projects);
                  const nextItems = [...base.items];
                  nextItems[editingItem.index] = updatedProject;
                  setProjectsDraft({ ...base, items: nextItems });
                  setProjectsDirty(true);
                  setEditingItem(null);
                }}
                onCancel={() => setEditingItem(null)}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderExperience = () => {
    const ex = experienceDraft ?? portfolioData.experience;
    const expItems = ex.items || [];

    const handleSaveExperience = async () => {
      const data = experienceDraft ?? portfolioData.experience;
      setExperienceSaving(true);
      try {
        await updateSection("experience", data);
        setExperienceDirty(false);
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus(""), 2000);
      } catch (error) {
        setSaveStatus("error");
        setTimeout(() => setSaveStatus(""), 2000);
      } finally {
        setExperienceSaving(false);
      }
    };

    return (
      <div className="admin-section">
        <div className="section-header">
          <div>
            <h3><i className="fas fa-briefcase"></i> Work Experience</h3>
            <p className="section-subtitle">Edit your timeline, then save to publish</p>
          </div>
          <div className="section-actions">
            <button
              className="btn-action btn-add"
              onClick={() => setShowAddForm(true)}
            >
              <i className="fas fa-plus"></i>
              <span>Add Experience</span>
            </button>
          </div>
          {saveStatus && (
            <div className={`save-indicator save-${saveStatus}`}>
              <i className={`fas fa-${saveStatus === "saved" ? "check-circle" : "exclamation-circle"}`}></i>
              {saveStatus === "saved" ? "Saved" : "Error"}
            </div>
          )}
        </div>

        {showAddForm && (
          <div className="add-form">
            <h4>Add New Experience</h4>
            <ExperienceForm
              experience={{}}
              onSave={(experience) => {
                const base = experienceDraft ?? deepClone(portfolioData.experience);
                setExperienceDraft({
                  ...base,
                  items: [...(base.items || []), experience],
                });
                setExperienceDirty(true);
                setShowAddForm(false);
              }}
              onCancel={() => setShowAddForm(false)}
            />
          </div>
        )}

        <div className="experience-timeline">
          {expItems.map((exp, index) => (
            <div key={index} className="experience-timeline-item">
              <div className="timeline-marker"></div>
              <div className="experience-card">
                <div className="experience-header">
                  <div className="experience-main">
                    <h5 className="experience-title">{exp.title}</h5>
                    <p className="experience-company">
                      <i className="fas fa-building"></i> {exp.company}
                    </p>
                    <p className="experience-period">
                      <i className="fas fa-calendar"></i> {exp.period}
                    </p>
                  </div>
                  <div className="experience-actions">
                    <button
                      className="btn-icon btn-primary-ghost"
                      onClick={() =>
                        setEditingItem({ type: "experience", index, data: exp })
                      }
                      title="Edit experience"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="btn-icon btn-danger-ghost"
                      onClick={() => {
                        if (
                          window.confirm(
                            "Remove this entry from the draft? Save changes to sync to Firestore."
                          )
                        ) {
                          const base = experienceDraft ?? deepClone(portfolioData.experience);
                          setExperienceDraft({
                            ...base,
                            items: base.items.filter((_, i) => i !== index),
                          });
                          setExperienceDirty(true);
                        }
                      }}
                      title="Delete experience"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
                <p className="experience-description">{exp.description}</p>
              </div>
            </div>
          ))}
        </div>

        {expItems.length === 0 && (
          <div className="empty-state">
            <i className="fas fa-briefcase"></i>
            <p>No experience entries yet. Click "Add Experience" to create one.</p>
          </div>
        )}

        <div className="form-card" style={{ marginTop: "1.25rem" }}>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={!experienceDirty || experienceSaving}
              onClick={handleSaveExperience}
            >
              <i className="fas fa-save"></i>
              {experienceSaving ? " Saving…" : " Save changes"}
            </button>
          </div>
        </div>

        {editingItem && editingItem.type === "experience" && (
          <div className="edit-modal">
            <div className="edit-modal-content">
              <h4>Edit Experience</h4>
              <ExperienceForm
                experience={editingItem.data}
                onSave={(updatedExperience) => {
                  const base = experienceDraft ?? deepClone(portfolioData.experience);
                  const nextItems = [...base.items];
                  nextItems[editingItem.index] = updatedExperience;
                  setExperienceDraft({ ...base, items: nextItems });
                  setExperienceDirty(true);
                  setEditingItem(null);
                }}
                onCancel={() => setEditingItem(null)}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMessages = () => {
    const deleteMessage = async (messageId) => {
      if (window.confirm("Are you sure you want to delete this message?")) {
        try {
          const { doc, deleteDoc } = await import('firebase/firestore');
          const { db } = await import('../config/firebase');
          await deleteDoc(doc(db, 'contactMessages', messageId));
          setMessages(messages.filter(msg => msg.id !== messageId));
        } catch (error) {
          console.error('Error deleting message:', error);
          alert('Failed to delete message. Please try again.');
        }
      }
    };

    const markAsRead = async (messageId, currentStatus) => {
      try {
        const { doc, updateDoc } = await import('firebase/firestore');
        const { db } = await import('../config/firebase');
        await updateDoc(doc(db, 'contactMessages', messageId), {
          read: !currentStatus
        });
        setMessages(messages.map(msg => 
          msg.id === messageId ? { ...msg, read: !currentStatus } : msg
        ));
      } catch (error) {
        console.error('Error updating message:', error);
      }
    };

    const formatDate = (date) => {
      if (!date) return 'Unknown';
      return new Date(date).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    const unreadCount = messages.filter(msg => !msg.read).length;

    return (
      <div className="admin-section">
        <div className="section-header">
          <div>
            <h3><i className="fas fa-inbox"></i> Contact Messages</h3>
            <p className="section-subtitle">Messages received from your portfolio contact form</p>
          </div>
          <div className="messages-stats">
            <span className="message-stat">
              <i className="fas fa-envelope"></i> Total: {messages.length}
            </span>
            <span className="message-stat unread">
              <i className="fas fa-envelope-open"></i> Unread: {unreadCount}
            </span>
          </div>
        </div>

        {isLoadingMessages ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading messages...</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-inbox"></i>
            <p>No messages yet. Messages from your contact form will appear here.</p>
          </div>
        ) : (
          <div className="messages-list">
            {messages.map((message) => (
              <div key={message.id} className={`message-card ${message.read ? 'read' : 'unread'}`}>
                <div className="message-header">
                  <div className="message-sender">
                    <div className="sender-avatar">
                      {message.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="sender-info">
                      <h5>{message.name || 'Anonymous'}</h5>
                      <p className="sender-email">
                        <i className="fas fa-envelope"></i> {message.email || 'No email provided'}
                      </p>
                    </div>
                  </div>
                  <div className="message-actions">
                    <button
                      className={`btn-icon ${message.read ? 'btn-secondary-ghost' : 'btn-primary-ghost'}`}
                      onClick={() => markAsRead(message.id, message.read)}
                      title={message.read ? 'Mark as unread' : 'Mark as read'}
                    >
                      <i className={`fas fa-${message.read ? 'envelope' : 'envelope-open'}`}></i>
                    </button>
                    <button
                      className="btn-icon btn-danger-ghost"
                      onClick={() => deleteMessage(message.id)}
                      title="Delete message"
                    >
                      <i className="fas fa-trash-alt"></i>
                    </button>
                  </div>
                </div>
                <div className="message-body">
                  <p>{message.message || 'No message content'}</p>
                </div>
                <div className="message-footer">
                  <span className="message-time">
                    <i className="fas fa-clock"></i> {formatDate(message.timestamp)}
                  </span>
                  {!message.read && <span className="unread-badge">New</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderAnalytics = () => {
    const handleRefreshStats = async () => {
      setIsLoadingStats(true);
      try {
        const stats = await getVisitorStats();
        setVisitorStats(stats);
      } catch (error) {
        console.error('Error refreshing stats:', error);
      } finally {
        setIsLoadingStats(false);
      }
    };

    const handleResetStats = async () => {
      if (window.confirm("⚠️ Are you sure you want to reset all visitor statistics? This action cannot be undone.")) {
        setIsLoadingStats(true);
        try {
          await resetVisitorStats();
          const stats = await getVisitorStats();
          setVisitorStats(stats);
        } catch (error) {
          console.error('Error resetting stats:', error);
        } finally {
          setIsLoadingStats(false);
        }
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return "Never";
      const date = new Date(dateString);
      return date.toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    };

    const formatNumber = (num) => {
      if (num >= 1000) {
        return (num / 1000).toFixed(1) + 'K';
      }
      return num.toString();
    };

    return (
      <div className="admin-section">
        <div className="section-header">
          <div>
            <h3><i className="fas fa-chart-line"></i> Visitor Analytics</h3>
            <p className="section-subtitle">Real-time portfolio traffic insights</p>
          </div>
          <div className="section-actions">
            <button 
              className="btn-action btn-refresh" 
              onClick={handleRefreshStats}
              disabled={isLoadingStats}
              title="Refresh stats"
            >
              <i className={`fas fa-sync-alt ${isLoadingStats ? 'fa-spin' : ''}`}></i>
              <span>Refresh</span>
            </button>
            <button 
              className="btn-action btn-danger" 
              onClick={handleResetStats}
              disabled={isLoadingStats}
              title="Reset all stats"
            >
              <i className="fas fa-trash-alt"></i>
              <span>Reset</span>
            </button>
          </div>
        </div>

        {isLoadingStats ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading analytics...</p>
          </div>
        ) : (
          <div className="analytics-grid">
            <div className="stat-card stat-primary">
              <div className="stat-icon">
                <i className="fas fa-eye"></i>
              </div>
              <div className="stat-content">
                <p className="stat-label">Total Visits</p>
                <h4 className="stat-number">{formatNumber(visitorStats.totalVisits)}</h4>
                <p className="stat-subtitle">All time page views</p>
              </div>
            </div>

            <div className="stat-card stat-success">
              <div className="stat-icon">
                <i className="fas fa-users"></i>
              </div>
              <div className="stat-content">
                <p className="stat-label">Unique Visitors</p>
                <h4 className="stat-number">{formatNumber(visitorStats.uniqueVisitors)}</h4>
                <p className="stat-subtitle">Estimated unique users</p>
              </div>
            </div>

            <div className="stat-card stat-info">
              <div className="stat-icon">
                <i className="fas fa-calendar-day"></i>
              </div>
              <div className="stat-content">
                <p className="stat-label">Today</p>
                <h4 className="stat-number">{formatNumber(visitorStats.todayVisits)}</h4>
                <p className="stat-subtitle">Visits today</p>
              </div>
            </div>

            <div className="stat-card stat-warning">
              <div className="stat-icon">
                <i className="fas fa-calendar-week"></i>
              </div>
              <div className="stat-content">
                <p className="stat-label">This Week</p>
                <h4 className="stat-number">{formatNumber(visitorStats.thisWeekVisits)}</h4>
                <p className="stat-subtitle">Last 7 days</p>
              </div>
            </div>

            <div className="stat-card stat-purple">
              <div className="stat-icon">
                <i className="fas fa-calendar-alt"></i>
              </div>
              <div className="stat-content">
                <p className="stat-label">This Month</p>
                <h4 className="stat-number">{formatNumber(visitorStats.thisMonthVisits)}</h4>
                <p className="stat-subtitle">Last 30 days</p>
              </div>
            </div>

            <div className="stat-card stat-secondary">
              <div className="stat-icon">
                <i className="fas fa-clock"></i>
              </div>
              <div className="stat-content">
                <p className="stat-label">Last Visit</p>
                <h4 className="stat-number stat-date">{formatDate(visitorStats.lastVisit)}</h4>
                <p className="stat-subtitle">Most recent activity</p>
              </div>
            </div>
          </div>
        )}

        <div className="info-banner">
          <i className="fas fa-database"></i>
          <p>Visitor data is stored in Firestore and synced in real-time across all devices.</p>
        </div>
      </div>
    );
  };

  // Return loading state if portfolioData is not yet available
  if (!portfolioData || isLoading) {
    return (
      <div className="admin-dashboard-overlay">
        <div className="loading-state" style={{ padding: '4rem', textAlign: 'center' }}>
          <i className="fas fa-spinner fa-spin" style={{ fontSize: '3rem', color: '#667eea', marginBottom: '1rem' }}></i>
          <p style={{ fontSize: '1.125rem', color: '#6b7280' }}>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-overlay">
      <div className="admin-dashboard">
        <div className="admin-header">
          <div className="admin-brand">
            <div className="admin-logo">
              <i className="fas fa-crown"></i>
            </div>
            <div className="admin-info">
              <h2>Admin Dashboard</h2>
              <p>Portfolio Management System</p>
            </div>
          </div>
          <div className="admin-actions">
            <button className="btn-header btn-logout" onClick={handleLogout}>
              <i className="fas fa-sign-out-alt"></i>
              <span>Logout</span>
            </button>
            <button className="btn-header btn-close" onClick={onClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        <div className="admin-content">
          <div className="admin-sidebar">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <i className={tab.icon}></i>
                {tab.name}
              </button>
            ))}
          </div>

          <div className="admin-main">
            {activeTab === "analytics" && renderAnalytics()}
            {activeTab === "messages" && renderMessages()}
            {activeTab === "personal" && renderPersonalInfo()}
            {activeTab === "about" && renderAbout()}
            {activeTab === "skills" && renderSkills()}
            {activeTab === "projects" && renderProjects()}
            {activeTab === "experience" && renderExperience()}
            {activeTab === "contact" && renderContact()}
          </div>
        </div>
      </div>
    </div>
  );
};

// Project Form Component
const inferFirebaseHostingUrl = (links) => {
  if (!links?.length) return "";
  const hit = links.find(
    (l) =>
      /firebase|hosting/i.test(String(l.text || "")) ||
      (String(l.icon || "").includes("cloud")) ||
      /web\.app|firebaseapp\.com/i.test(String(l.url || ""))
  );
  const u = hit?.url?.trim();
  return u && u !== "#" ? u : "";
};

const normalizeProjectLinksDraft = (links) =>
  (links?.length ? links : []).map((l) => ({
    text: l.text || "",
    url: l.url || "",
    icon: l.icon || "fas fa-external-link-alt",
    type: l.type === "secondary" ? "secondary" : "primary",
  }));

const ProjectForm = ({ project, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: project.title || "",
    description: project.description || "",
    icon: project.icon || "📱",
    category: project.category || "mobile",
    status: project.status || "Live",
    users: project.users || "",
    rating: project.rating || "",
    tech: project.tech ? project.tech.join(", ") : "",
    features: project.features ? project.features.join(", ") : "",
  });
  const [linksList, setLinksList] = useState(() =>
    normalizeProjectLinksDraft(project.links)
  );
  const [firebaseHostingUrl, setFirebaseHostingUrl] = useState(() =>
    inferFirebaseHostingUrl(project.links)
  );

  useEffect(() => {
    setFormData({
      title: project.title || "",
      description: project.description || "",
      icon: project.icon || "📱",
      category: project.category || "mobile",
      status: project.status || "Live",
      users: project.users || "",
      rating: project.rating || "",
      tech: project.tech ? project.tech.join(", ") : "",
      features: project.features ? project.features.join(", ") : "",
    });
    setLinksList(normalizeProjectLinksDraft(project.links));
    setFirebaseHostingUrl(inferFirebaseHostingUrl(project.links));
  }, [project]);

  const handleSubmit = (e) => {
    e.preventDefault();
    let linksOut = linksList
      .map((l) => ({
        text: (l.text || "").trim() || "Link",
        url: (l.url || "").trim(),
        icon: (l.icon || "").trim() || "fas fa-external-link-alt",
        type: l.type === "secondary" ? "secondary" : "primary",
      }))
      .filter((l) => l.url);

    const fh = firebaseHostingUrl.trim();
    if (fh) {
      const firebaseLink = {
        text: "Firebase Hosting",
        url: fh,
        icon: "fas fa-cloud",
        type: "secondary",
      };
      const idx = linksOut.findIndex(
        (l) =>
          /firebase|hosting/i.test(l.text) ||
          (l.icon && l.icon.includes("cloud")) ||
          /web\.app|firebaseapp\.com/i.test(l.url)
      );
      if (idx >= 0) linksOut[idx] = firebaseLink;
      else linksOut.push(firebaseLink);
    }

    const projectData = {
      ...formData,
      tech: formData.tech
        .split(",")
        .map((t) => t.trim())
        .filter((t) => t),
      features: formData.features
        .split(",")
        .map((f) => f.trim())
        .filter((f) => f),
      links: linksOut,
    };
    onSave(projectData);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const addLinkRow = () => {
    setLinksList((prev) => [
      ...prev,
      {
        text: "",
        url: "",
        icon: "fas fa-external-link-alt",
        type: "primary",
      },
    ]);
  };

  const removeLinkRow = (index) => {
    setLinksList((prev) => prev.filter((_, i) => i !== index));
  };

  const updateLinkRow = (index, field, value) => {
    setLinksList((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  return (
    <form onSubmit={handleSubmit} className="project-form">
      <p style={{ fontSize: "0.85rem", color: "#6b7280", marginBottom: "1rem" }}>
        Apply updates here, then use <strong>Save changes</strong> on the Projects tab to sync to Firestore.
      </p>
      <div className="form-row">
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Icon</label>
          <input
            type="text"
            name="icon"
            value={formData.icon}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="3"
          required
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Category</label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
          >
            <option value="mobile">Mobile</option>
            <option value="web">Web</option>
            <option value="game">Game</option>
          </select>
        </div>
        <div className="form-group">
          <label>Status</label>
          <select name="status" value={formData.status} onChange={handleChange}>
            <option value="Live">Live</option>
            <option value="Development">Development</option>
            <option value="Beta">Beta</option>
          </select>
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Users</label>
          <input
            type="text"
            name="users"
            value={formData.users}
            onChange={handleChange}
            placeholder="e.g., 10K+"
          />
        </div>
        <div className="form-group">
          <label>Rating</label>
          <input
            type="text"
            name="rating"
            value={formData.rating}
            onChange={handleChange}
            placeholder="e.g., 4.8"
          />
        </div>
      </div>

      <div className="form-group">
        <label>Technologies (comma separated)</label>
        <input
          type="text"
          name="tech"
          value={formData.tech}
          onChange={handleChange}
          placeholder="Flutter, Node.js, MongoDB"
        />
      </div>

      <div className="form-group">
        <label>Features (comma separated)</label>
        <input
          type="text"
          name="features"
          value={formData.features}
          onChange={handleChange}
          placeholder="Real-time Chat, Multi-language"
        />
      </div>

      <div className="form-section" style={{ marginTop: "1rem" }}>
        <div className="subsection-header" style={{ marginBottom: "0.75rem" }}>
          <h4 className="form-section-title" style={{ margin: 0 }}>
            <i className="fas fa-link"></i> Store &amp; web links
          </h4>
          <button type="button" className="btn-action btn-add" onClick={addLinkRow}>
            <i className="fas fa-plus"></i>
            <span>Add link</span>
          </button>
        </div>
        {linksList.length === 0 && (
          <p style={{ fontSize: "0.875rem", color: "#6b7280" }}>
            No links yet. Add Play Store, App Store, website, or other URLs.
          </p>
        )}
        {linksList.map((link, index) => (
          <div
            key={index}
            className="form-row"
            style={{
              alignItems: "flex-end",
              borderBottom: "1px solid #e5e7eb",
              paddingBottom: "0.75rem",
              marginBottom: "0.75rem",
            }}
          >
            <div className="form-group">
              <label>Button label</label>
              <input
                type="text"
                value={link.text}
                onChange={(e) => updateLinkRow(index, "text", e.target.value)}
                placeholder="Play Store"
              />
            </div>
            <div className="form-group" style={{ flex: 2 }}>
              <label>URL</label>
              <input
                type="url"
                value={link.url}
                onChange={(e) => updateLinkRow(index, "url", e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label>Icon class</label>
              <input
                type="text"
                value={link.icon}
                onChange={(e) => updateLinkRow(index, "icon", e.target.value)}
                placeholder="fab fa-google-play"
              />
            </div>
            <div className="form-group">
              <label>Style</label>
              <select
                value={link.type}
                onChange={(e) => updateLinkRow(index, "type", e.target.value)}
              >
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
              </select>
            </div>
            <button
              type="button"
              className="btn-icon btn-danger-ghost"
              onClick={() => removeLinkRow(index)}
              title="Remove link"
            >
              <i className="fas fa-trash-alt"></i>
            </button>
          </div>
        ))}
      </div>

      <div className="form-group" style={{ marginTop: "1rem" }}>
        <label>
          <i className="fas fa-cloud"></i> Firebase Hosting URL (optional)
        </label>
        <input
          type="url"
          value={firebaseHostingUrl}
          onChange={(e) => setFirebaseHostingUrl(e.target.value)}
          placeholder="https://your-app.web.app"
        />
        <span className="char-count">
          Adds or updates a &quot;Firebase Hosting&quot; button on the portfolio card (after you save the Projects tab).
        </span>
      </div>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary">
          <i className="fas fa-check"></i> Apply to draft
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          <i className="fas fa-times"></i> Cancel
        </button>
      </div>
    </form>
  );
};

const SkillCategoryForm = ({ category, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    category: category.category || "",
    icon: category.icon || "",
    description: category.description || "",
    skills: category.skills || [],
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSkillChange = (index, field, value) => {
    const newSkills = [...formData.skills];
    newSkills[index] = { ...newSkills[index], [field]: value };
    setFormData({ ...formData, skills: newSkills });
  };

  const addSkill = () => {
    setFormData({
      ...formData,
      skills: [...formData.skills, { name: "", level: 50, color: "#3B82F6" }],
    });
  };

  const removeSkill = (index) => {
    const newSkills = formData.skills.filter((_, i) => i !== index);
    setFormData({ ...formData, skills: newSkills });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="project-form">
      <div className="form-row">
        <div className="form-group">
          <label>Category Name</label>
          <input
            type="text"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Icon (Emoji)</label>
          <input
            type="text"
            name="icon"
            value={formData.icon}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="2"
          required
        />
      </div>

      <div className="form-group full-width">
        <label>Skills</label>
        {formData.skills.map((skill, index) => (
          <div
            key={index}
            className="form-row"
            style={{ marginBottom: "0.5rem" }}
          >
            <input
              type="text"
              value={skill.name}
              onChange={(e) => handleSkillChange(index, "name", e.target.value)}
              placeholder="Skill Name"
              style={{ flex: 1 }}
              required
            />
            <input
              type="number"
              value={skill.level}
              onChange={(e) =>
                handleSkillChange(index, "level", parseInt(e.target.value))
              }
              placeholder="Level (0-100)"
              min="0"
              max="100"
              style={{ flex: 0.5 }}
              required
            />
            <input
              type="color"
              value={skill.color}
              onChange={(e) =>
                handleSkillChange(index, "color", e.target.value)
              }
              style={{ flex: 0.3 }}
            />
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => removeSkill(index)}
            >
              <i className="fas fa-minus"></i>
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={addSkill}
        >
          <i className="fas fa-plus"></i> Add Skill
        </button>
      </div>

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Save
        </button>
      </div>
    </form>
  );
};

const ContactMethodForm = ({ method, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: method.name || "",
    url: method.url || "",
    icon: method.icon || "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="project-form">
      <div className="form-group">
        <label>Method Name</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <label>URL</label>
        <input
          type="url"
          name="url"
          value={formData.url}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <label>Icon (Font Awesome class)</label>
        <input
          type="text"
          name="icon"
          value={formData.icon}
          onChange={handleChange}
          placeholder="fab fa-whatsapp"
          required
        />
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Save
        </button>
      </div>
    </form>
  );
};

const FormFieldForm = ({ field, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    label: field.label || "",
    placeholder: field.placeholder || "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="project-form">
      <div className="form-group">
        <label>Field Label</label>
        <input
          type="text"
          name="label"
          value={formData.label}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-group">
        <label>Placeholder Text</label>
        <input
          type="text"
          name="placeholder"
          value={formData.placeholder}
          onChange={handleChange}
          required
        />
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Save
        </button>
      </div>
    </form>
  );
};

const ExperienceForm = ({ experience, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    period: experience.period || "",
    title: experience.title || "",
    company: experience.company || "",
    description: experience.description || "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="project-form">
      <div className="form-group">
        <label>Period</label>
        <input
          type="text"
          name="period"
          value={formData.period}
          onChange={handleChange}
          placeholder="e.g., 2021 - Present"
          required
        />
      </div>
      <div className="form-group">
        <label>Title</label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="e.g., Senior Full Stack Developer"
          required
        />
      </div>
      <div className="form-group">
        <label>Company</label>
        <input
          type="text"
          name="company"
          value={formData.company}
          onChange={handleChange}
          placeholder="e.g., Tech Solutions Inc."
          required
        />
      </div>
      <div className="form-group">
        <label>Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows="4"
          placeholder="Describe your role and achievements..."
          required
        />
      </div>
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          Save
        </button>
      </div>
    </form>
  );
};

export default AdminDashboard;
